import {
  invalidateCustomer,
  useDeleteApprovedEquipmentCustomer,
  useGetApprovedEquipmentCustomers,
} from 'src/api/customer'
import { StyledCard } from 'src/screens/localities/maintenance/EquipmentMaintenanceContainer'
import { GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDeleteOutline } from 'react-icons/md'
import GenericTable from 'src/components/table/GenericTable'
import { getFormattedDate } from 'src/utils'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { toast } from 'react-toastify'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { CenteredSpinner } from 'src/components/Spinner'
import { ShowDialogProps } from 'src/show-dialog'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { HowToReg } from '@mui/icons-material'
import ApprovedEquipmentCustomersDialog from 'src/components/main-component/ApproveEquipmentCustomersDialog'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { CancelButton } from 'src/components/CancelButton'
interface ApprovedEquipmentCustomersProps extends ShowDialogProps {
  search?: string
  customerId?: EntityId
}
const ApprovedEquipmentCustomers: React.FC<ApprovedEquipmentCustomersProps> = ({ search, pop, customerId }) => {
  const { data: approvedCustomers, isLoading } = useGetApprovedEquipmentCustomers(customerId as EntityId, search)
  const { t: translate } = useTranslation(['main-component', 'global-documents', 'common'])
  const { showConfirmDialog, showDialog } = useDialogCtx()
  const { mutate: deleteApprovedCustomer } = useDeleteApprovedEquipmentCustomer(customerId as EntityId)

  const onDeleteApprovedCustomer = useCallback(
    (id: EntityId) => {
      showConfirmDialog(
        translate('delete-approved-customer', { ns: 'main-component' }),
        translate('areYouSureDeleteApprovedCustomer', { ns: 'main-component' }),
        { acceptText: translate('yes', { ns: 'common' }), cancelText: translate('no', { ns: 'common' }) },
        async pop => {
          await deleteApprovedCustomer(id, {
            onSuccess: () => {
              toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
              invalidateCustomer.getApprovedEquipmentCustomers(customerId as EntityId)
              invalidateCustomer.getCustomersForEquipmentApproval(customerId as EntityId)
              pop()
            },
            onError: (error: any) => {
              toast.error(translate('error-deleting-approved-customer', { ns: 'main-component' }))
            },
          })
        },
      )
    },
    [showConfirmDialog, translate, deleteApprovedCustomer, customerId],
  )

  const handleAddApprovedCustomers = useCallback(() => {
    showDialog(
      ApprovedEquipmentCustomersDialog,
      {
        opts: { maxWidth: 'md', fullWidth: true },
        componentProps: {
          customerId: customerId as EntityId,
        },
      },
      undefined,
      true,
    )
  }, [customerId, showDialog])

  const STATIC_COLUMNS: GridColumns = [
    { field: 'name', headerName: translate('name', { ns: 'common' }), flex: 2 },
    {
      field: 'createdAt',
      headerName: translate('approvedAt', { ns: 'global-documents' }),
      minWidth: 170,
      flex: 1,
      renderCell: ({ row }) => (row?.createdAt ? getFormattedDate(new Date(row?.createdAt + 'Z'), 'YYYY-MM-DD') : ''),
    },
    {
      field: 'createdBy',
      headerName: translate('approvedBy', { ns: 'global-documents' }),
      flex: 1,
      renderCell: ({ row }) => (row?.createdByUser ? getUserDetail(row?.createdByUser, UserDetail.FullName) : ''),
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 100,
        renderCell: ({ row }) => [
          <Tooltip title={`${translate('delete', { ns: 'common' })}`} key={'delete'}>
            <IconButton onClick={() => onDeleteApprovedCustomer(row.id)} color="primary">
              <MdDeleteOutline size={24} />
            </IconButton>
          </Tooltip>,
        ],
      },
    ],
    [],
  )

  return (
    <GenericDialogLayout
      title={''}
      pop={pop}
      actions={[<CancelButton onClick={pop} key={'cancel-btn'} data-cy="Cancel" />]}
    >
      <CommonPageLayout
        titleSection={{ subtitle: translate('approved-customers', { ns: 'main-component' }) }}
        topAction={[
          <GridActionButton
            key="approve-customers"
            buttonText="customers"
            onClick={handleAddApprovedCustomers}
            height="54px"
            buttonIcon={<HowToReg />}
            toolTipTitle={translate('approve-customers', { ns: 'main-component' })}
          />,
        ]}
      >
        <StyledCard
          className="p-2 mb-2"
          sx={{ minHeight: '400px', height: isLoading ? '400px' : 'auto', paddingTop: '30px' }}
        >
          {isLoading ? (
            <CenteredSpinner height={400} />
          ) : (
            <GenericTable
              id="approved-customers"
              data={approvedCustomers}
              columns={columns}
              getRowHeight={() => 44}
              autoHeight
              noDataComponent={() => (
                <Box height="400px">
                  <EmptyStatusCard
                    imageWidth="260px"
                    header={translate('approved-customer-empty-status-title', { ns: 'main-component' })}
                    content={translate('approved-customer-empty-status-content', { ns: 'main-component' })}
                    headerFontSize="18px"
                    contentFontSize="14px"
                    contentClasses="mt-2"
                    contentTopMargin="10px"
                    imageContainerClasses="pb-1 pt-1"
                    height="100%"
                  />
                </Box>
              )}
            />
          )}
        </StyledCard>
      </CommonPageLayout>
    </GenericDialogLayout>
  )
}

export default ApprovedEquipmentCustomers
